import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Layout } from "../components/Layout";
import { Plus, Trash2, Download, FileText, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD"];
const SYM = { USD: "$", EUR: "€", GBP: "£", CAD: "$", AUD: "$" };

const emptyInvoice = () => ({
  businessName: "", customerName: "", customerEmail: "",
  items: [{ description: "", qty: 1, unitPrice: 0 }],
  taxPercent: 0, notes: "", dueDate: "", currency: "USD",
});

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState(null); // null = list view
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/invoices"); setInvoices(data); };
  useEffect(() => { load(); }, []);

  const sym = SYM[form?.currency] || "$";
  const subtotal = form ? form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unitPrice) || 0), 0) : 0;
  const tax = form ? subtotal * (Number(form.taxPercent) || 0) / 100 : 0;
  const total = subtotal + tax;

  const setItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", qty: 1, unitPrice: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const openNew = () => { setEditingId(null); setForm(emptyInvoice()); };
  const openEdit = (inv) => { setEditingId(inv.id); setForm({ ...emptyInvoice(), ...inv }); };

  const save = async () => {
    if (!form.customerName) { toast.error("Customer name is required"); return null; }
    setBusy(true);
    try {
      const payload = { ...form, customerEmail: form.customerEmail || "none@none.com",
        items: form.items.map((i) => ({ description: i.description, qty: Number(i.qty), unitPrice: Number(i.unitPrice) })) };
      const res = editingId ? await api.put(`/invoices/${editingId}`, payload) : await api.post("/invoices", payload);
      await load();
      setEditingId(res.data.id);
      setForm({ ...emptyInvoice(), ...res.data });
      toast.success("Invoice saved");
      return res.data;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); return null; }
    finally { setBusy(false); }
  };

  const downloadPdf = async (iid) => {
    const res = await api.get(`/invoices/${iid}/pdf`, { responseType: "blob" });
    const inv = invoices.find((x) => x.id === iid);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${inv?.invoiceNumber || "invoice"}.pdf`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Invoice downloaded");
  };

  const saveAndDownload = async () => {
    const saved = await save();
    if (saved) await downloadPdf(saved.id);
  };

  const remove = async (iid) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${iid}`);
    setInvoices((s) => s.filter((x) => x.id !== iid));
    toast.success("Invoice deleted");
  };

  // ---------- FORM VIEW ----------
  if (form) {
    return (
      <Layout>
        <div className="fadeup space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-head font-bold text-2xl sm:text-3xl">{editingId ? "Edit invoice" : "New invoice"}</h2>
            <button onClick={() => { setForm(null); setEditingId(null); }} className="pm-btn-ghost text-sm flex items-center gap-1.5" data-testid="invoice-close"><X className="w-4 h-4" /> Close</button>
          </div>

          <div className="pm-card p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">Your business name
                <input className="pm-input mt-1" value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} data-testid="inv-business" /></label>
              <label className="text-sm">Currency
                <select className="pm-input mt-1" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="inv-currency">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></label>
              <label className="text-sm">Customer name *
                <input className="pm-input mt-1" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} data-testid="inv-customer-name" /></label>
              <label className="text-sm">Customer email
                <input className="pm-input mt-1" type="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} data-testid="inv-customer-email" /></label>
              <label className="text-sm">Due date
                <input className="pm-input mt-1" type="date" value={form.dueDate || ""} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} data-testid="inv-due" /></label>
              <label className="text-sm">Tax %
                <input className="pm-input mt-1 font-mono" type="number" min="0" step="0.01" value={form.taxPercent} onChange={(e) => setForm({ ...form, taxPercent: e.target.value })} data-testid="inv-tax" /></label>
            </div>

            <div>
              <div className="label-mono mb-2">Line items</div>
              <div className="space-y-2" data-testid="inv-items">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input className="pm-input col-span-6 text-sm" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} />
                    <input className="pm-input col-span-2 text-sm font-mono" type="number" min="0" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`item-qty-${i}`} />
                    <input className="pm-input col-span-3 text-sm font-mono" type="number" min="0" step="0.01" placeholder="Unit price" value={it.unitPrice} onChange={(e) => setItem(i, "unitPrice", e.target.value)} data-testid={`item-price-${i}`} />
                    <button onClick={() => removeItem(i)} className="col-span-1 flex justify-center" data-testid={`item-remove-${i}`}><Trash2 className="w-4 h-4" style={{ color: "var(--warning)" }} /></button>
                  </div>
                ))}
              </div>
              <button onClick={addItem} className="pm-btn-ghost text-sm mt-2 flex items-center gap-1.5" data-testid="add-item-btn"><Plus className="w-4 h-4" /> Add line item</button>
            </div>

            <label className="text-sm block">Notes
              <textarea className="pm-input mt-1" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="inv-notes" /></label>

            <div className="flex justify-end">
              <table className="text-sm font-mono">
                <tbody>
                  <tr><td className="pr-8 text-right py-0.5">Subtotal</td><td className="text-right" data-testid="inv-subtotal">{sym}{subtotal.toFixed(2)}</td></tr>
                  <tr><td className="pr-8 text-right py-0.5">Tax</td><td className="text-right">{sym}{tax.toFixed(2)}</td></tr>
                  <tr className="font-bold"><td className="pr-8 text-right py-0.5 border-t" style={{ borderColor: "var(--border)" }}>Total</td><td className="text-right border-t" style={{ borderColor: "var(--border)" }} data-testid="inv-total">{sym}{total.toFixed(2)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={busy} className="pm-btn-ghost flex items-center gap-2" data-testid="save-invoice-btn"><FileText className="w-4 h-4" /> Save</button>
            {editingId && <button onClick={() => downloadPdf(editingId)} className="pm-btn-ghost flex items-center gap-2" data-testid="download-pdf-btn"><Download className="w-4 h-4" /> Download PDF</button>}
            <button onClick={saveAndDownload} disabled={busy} className="pm-btn flex items-center gap-2" data-testid="complete-invoice-btn"><CheckCircle2 className="w-4 h-4" /> Complete & download</button>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- LIST VIEW ----------
  return (
    <Layout>
      <div className="fadeup space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-head font-bold text-2xl sm:text-3xl">Invoices</h2>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Build an invoice and download it as a PDF to send or print.</p>
          </div>
          <button onClick={openNew} className="pm-btn flex items-center gap-2" data-testid="new-invoice-btn"><Plus className="w-4 h-4" /> New invoice</button>
        </div>

        {invoices.length === 0 ? (
          <div className="pm-card p-8 text-center" data-testid="no-invoices">
            <FileText className="w-7 h-7 mx-auto mb-3" />
            <div className="font-head font-bold">No invoices yet</div>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Create your first invoice to bill a customer.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="invoices-list">
            {invoices.map((inv) => (
              <div key={inv.id} className="pm-card p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`invoice-${inv.id}`}>
                <div>
                  <div className="font-mono font-semibold">{inv.invoiceNumber}</div>
                  <div className="text-sm mt-1">{inv.customerName}{inv.customerEmail && inv.customerEmail !== "none@none.com" ? ` · ${inv.customerEmail}` : ""}</div>
                  <div className="font-mono text-sm mt-0.5">{SYM[inv.currency] || "$"}{Number(inv.total).toFixed(2)}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => openEdit(inv)} className="pm-btn-ghost text-sm" data-testid={`edit-${inv.id}`}>Edit</button>
                  <button onClick={() => downloadPdf(inv.id)} className="pm-btn text-sm flex items-center gap-1.5" data-testid={`pdf-${inv.id}`}><Download className="w-4 h-4" /> Download PDF</button>
                  <button onClick={() => remove(inv.id)} className="pm-btn-ghost text-sm" style={{ borderColor: "var(--warning)", color: "var(--warning)" }} data-testid={`del-${inv.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
